import React, { useState, useEffect } from 'react';
import { ref, get, update } from 'firebase/database';
import { database } from '../../services/firebase';
import JobApplication from './JobApplication';
import { FaBuilding, FaMapMarkerAlt, FaMoneyBillWave, FaClock } from 'react-icons/fa';
import { analyzeResume } from '../../services/geminiService';

// Add new imports
import { useNavigate } from 'react-router-dom';
import { FaCalendar, FaPlay } from 'react-icons/fa';

const JobCard = ({ job }) => {
  const [showApplication, setShowApplication] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [atsScore, setAtsScore] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    checkApplicationStatus();
  }, []);
  const handleApplicationClose = () => {
    setShowApplication(false);
    checkApplicationStatus(); // Refresh application status
  };
  // Add new states
  const [testDate, setTestDate] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const navigate = useNavigate();

  // Update checkApplicationStatus
  const checkApplicationStatus = async () => {
    if (!userId) return;
    try {
      const userRef = ref(database, `users/${userId}/applications`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const applications = Object.values(snapshot.val());
        const existingApplication = applications.find(app => app.jobId === job.id);
        
        if (existingApplication) {
          setHasApplied(true);
          if (existingApplication.atsScore) {
            setAtsScore(existingApplication.atsScore);
          }
          if (existingApplication.testDate) {
            setTestDate(existingApplication.testDate);
          }
          if (existingApplication.testStatus) {
            setTestStatus(existingApplication.testStatus);
          }
        }
      }
    } catch (error) {
      console.error('Error checking application status:', error);
    }
  };

  const handleApplyClick = () => {
    if (!userId) {
      alert('Please login to apply for this job');
      return;
    }
    
    if (!hasApplied) {
      setShowApplication(true);
    }
  };

  const handleApplicationSubmit = async (resumeText, resumeUrl) => {
    setIsSubmitting(true);
    
    try {
      // Analyze resume with Gemini AI
      const analysis = await analyzeResume(resumeText, job.requirements);
      
      if (!analysis.canApply) {
        alert(`Cannot submit application: ${analysis.explanation}. Score: ${analysis.score}`);
        setIsSubmitting(false);
        return false;
      }

      // Prepare application data
      const applicationData = {
        appliedAt: new Date().toISOString(),
        jobId: job.id,
        resumeUrl,
        status: "pending",
        userId,
        atsScore: analysis.score,
        atsExplanation: analysis.explanation
      };

      // Save to database
      const userRef = ref(database, `users/${userId}/applications`);
      await update(userRef, { [Date.now()]: applicationData });
      
      setAtsScore(analysis.score);
      setHasApplied(true);
      setShowApplication(false);
      alert("Application submitted successfully!");
      return true;
    } catch (error) {
      console.error("Error submitting application:", error);
      alert("Error submitting application. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <FaBuilding className="text-indigo-600 text-xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{job.position}</h3>
              <p className="text-gray-600">{job.company}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm ${
            job.type === 'Full-time' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {job.type}
          </span>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center text-gray-600">
            <FaMapMarkerAlt className="mr-2" />
            {job.location}
          </div>
          <div className="flex items-center text-gray-600">
            <FaMoneyBillWave className="mr-2" />
            ₹{job.salary}
          </div>
          <div className="flex items-center text-gray-600">
            <FaClock className="mr-2" />
            Apply by {new Date(job.deadline).toLocaleDateString()}
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <h4 className="font-semibold text-gray-800">Requirements:</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            {job.requirements.map((req, index) => (
              <li key={index}>{req}</li>
            ))}
          </ul>
        </div>

        <button
          onClick={handleApplyClick}
          disabled={hasApplied}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 
            ${hasApplied 
              ? 'bg-green-100 text-green-800 cursor-not-allowed' 
              : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800'
            }`}
        >
          {hasApplied ? 'Already Applied' : 'Apply Now'}
        </button>
      </div>

      {showApplication && (
        <JobApplication
          job={job}
          onClose={handleApplicationClose}
          userId={userId}
          onSubmit={handleApplicationSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export default JobCard;